<script setup lang="ts">
  import { breakpointsTailwind, useBreakpoints, useTimeoutPoll } from '@vueuse/core';
  import * as changeCase from 'change-case';
  import dayjs from 'dayjs';
  import Mark from 'mark.js';
  import { storeToRefs } from 'pinia';
  import pluralize from 'pluralize';
  import {
    computed,
    inject,
    nextTick,
    onBeforeMount,
    onBeforeUnmount,
    ref,
    useTemplateRef,
    watch,
  } from 'vue';
  import { useRoute, useRouter } from 'vue-router';

  import type {
    Call,
    Email,
    EmailSender,
    EmailThread,
    Invoice,
    Partner,
    ResponseTaskData,
    Task,
    TaskRequirement,
  } from '@stuut/api/models';
  import { stripHTML } from '@stuut/common/src/utils';
  import { Alert, AlertDescription } from '@ui/components/alert';
  import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
  } from '@ui/components/alert-dialog';
  import { Button } from '@ui/components/button';
  import { ConfirmDialog } from '@ui/components/confirm-dialog';
  import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@ui/components/resizable';
  import { ScrollArea } from '@ui/components/scroll-area';
  import { Skeleton } from '@ui/components/skeleton';
  import { toast } from '@ui/components/sonner';
  import { Timestamp } from '@ui/components/time';
  import { ToggleGroup, ToggleGroupItem } from '@ui/components/toggle-group';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';
  import { cn } from '@ui/utils/utils.ts';

  import { stuut } from '@/api';
  import DisputeUpsellCard from '@/components/disputes/DisputeUpsellCard.vue';
  import LinkEmailDialog from '@/components/email-senders/LinkEmailDialog.vue';
  import { EmailThreadDrawer } from '@/components/email-threads';
  import EmailAutoResponse from '@/components/emails/EmailAutoResponse.vue';
  import InvoiceCompact from '@/components/tasks/InvoiceCompact.vue';
  import PaymentPromiseSubtasks from '@/components/tasks/PaymentPromiseSubtasks.vue';
  import RelatedTasks from '@/components/tasks/RelatedTasks.vue';
  import PaginatedTaskDrawerTable from '@/components/tasks/table/PaginatedTaskDrawerTable.vue';
  import TaskBrief from '@/components/tasks/TaskBrief.vue';
  import TaskBrokenPromisedPayments from '@/components/tasks/TaskBrokenPromisedPayments.vue';
  import TaskCallContext from '@/components/tasks/TaskCallContext.vue';
  import TaskCompact from '@/components/tasks/TaskCompact.vue';
  import TaskCompleted from '@/components/tasks/TaskCompleted.vue';
  import TaskDisableContactView from '@/components/tasks/TaskDisableContactView.vue';
  import TaskDismissAlert from '@/components/tasks/TaskDismissAlert.vue';
  import TaskDisputeForm from '@/components/tasks/TaskDisputeForm.vue';
  import TaskDrawerTablePagination from '@/components/tasks/TaskDrawerTablePagination.vue';
  import TaskEmailContext from '@/components/tasks/TaskEmailContext.vue';
  import TaskNewContact from '@/components/tasks/TaskNewContact.vue';
  import TaskPauseStateUpdateView from '@/components/tasks/TaskPauseStateUpdateView.vue';
  import TaskPromisedPayments from '@/components/tasks/TaskPromisedPayments.vue';
  import TaskRequirements from '@/components/tasks/TaskRequirements.vue';
  import TaskResolution from '@/components/tasks/TaskResolution.vue';
  import TaskSmsContext from '@/components/tasks/TaskSmsContext.vue';
  import TaskSmsResponse from '@/components/tasks/TaskSmsResponse.vue';
  import TasksToolbar from '@/components/tasks/TasksToolbar.vue';
  import TaskThinking from '@/components/tasks/TaskThinking.vue';
  import TaskTitle from '@/components/tasks/TaskTitle.vue';
  import { TaskTypeMap } from '@/constants/TaskConstants';
  import {
    useDisputeStore,
    useEmailSenders,
    useProfile,
    useTaskCollectionStore,
    useTaskStore,
  } from '@/store';
  import { useFeatureFlags } from '@/store/featureFlags';

  interface EvidenceQuery {
    id: string;
    text: string;
    type: string;
  }

  interface Props {
    invoices?: Invoice[];
    isCollectionView?: boolean;
    task?: Task;
  }

  const props = defineProps<Props>();
  const route = useRoute();
  const router = useRouter();
  const taskDisputeFormRef =
    useTemplateRef<InstanceType<typeof TaskDisputeForm>>('TaskDisputeFormRef');

  const formDataRef = ref();
  const featureFlagsStore = useFeatureFlags();

  const taskStore = useTaskStore();
  const disputeStore = useDisputeStore();
  const taskCollectionStore = useTaskCollectionStore();
  const profileStore = useProfile();
  const emailSenderStore = useEmailSenders();
  const showEmailThread = ref(false);
  const { profile } = storeToRefs(profileStore);
  const { emailSenders } = storeToRefs(emailSenderStore);
  const editMode = ref(false);
  const showDismissAlert = ref(false);
  const dismissAlertActionType = ref<string | undefined>();
  const showCompleteWarning = ref(false);

  const {
    currentCollectionTasksTotal,
    currentModalTaskType,
    currentTask,
    isGroupView,
    isIndividualTaskView,
    isTaskListMode,
    isTaskStackedMode,
    selectedTaskIndex,
  } = storeToRefs(taskCollectionStore);

  const componentTask = computed(() => props.task ?? currentTask.value);
  const validSenders = ref<EmailSender[]>([]);

  const showThinking = ref(false);
  const sidebarView = ref<'context' | 'invoices' | 'tasks'>('context');

  watch(
    () => componentTask.value?.isProcessing,
    (isProc) => {
      if (isProc) showThinking.value = true;
    },
    { immediate: true },
  );

  const onThinkingDone = () => {
    showThinking.value = false;
  };

  const isDisputesPro = computed(() => profileStore?.profile?.isDisputesEnabled ?? false);

  const TaskTableDrawerRef = ref();
  const TaskNewContactRef = ref();
  const TaskDisableContactRef = ref();
  const TaskSmsResponseRef = ref();

  const email = ref<Email | null>(null);
  const emailThread = ref<EmailThread | null>(null);
  const call = ref<Call | null>(null);

  const taskRequirements = computed<TaskRequirement[]>(
    () => componentTask.value?.hitl_questions ?? [],
  );

  const hasRequirementsModifiedAfterTask = computed(() => {
    const taskModifiedAt = componentTask.value?.modified_at;

    if (!taskModifiedAt) return false;

    return taskRequirements.value.some(
      (req) => req.modified_at && req.modified_at.isAfter(taskModifiedAt),
    );
  });

  const partnerId = ref<string | null>(componentTask.value?.partner_id ?? null);
  const partner = ref<Partner>();
  const showConfirmDialog = ref(false);
  const showCancelAlert = ref(false);
  const isSubmitting = ref<boolean>(false);
  const closeDrawer = inject<() => void>('closeDrawer');
  const evidenceQueries = ref<EvidenceQuery[] | []>([]);
  const loadingEmail = ref(false);
  const emailId = ref<string | undefined>(componentTask.value?.emailData?.id);
  const callId = ref<string | undefined>(componentTask.value?.callData?.id);
  const linkEmailDialogRef = ref();
  const PartnerOverviewRef = ref();
  const isBulkCompleting = ref(false);
  const PaymentPromiseSubtasksRef = ref();
  const isReplyRequired = computed(
    () =>
      componentTask.value?.isReplyRequired &&
      (componentTask.value?.responseData || componentTask.value?.isSmsSource),
  );

  const isResponseGenerating = computed(() => Boolean(formDataRef.value?.isGenerating));

  const hasContext = computed(
    () =>
      componentTask.value?.isEmailSource ||
      componentTask.value?.isCallSource ||
      componentTask.value?.isSmsSource,
  );

  const showBriefInSidebar = computed(
    () =>
      !componentTask.value?.is_group ||
      componentTask.value?.isPaymentPromise ||
      componentTask.value?.isPaymentPromiseGroup ||
      (componentTask.value?.isReplyRequired &&
        !!componentTask.value?.responseData?.data &&
        taskHitlEnabled.value),
  );

  const showPaymentPromiseReply = computed(() => {
    if (!componentTask.value?.isPaymentPromise || !componentTask.value?.isEmailSource) return false;
    const relatedTasks = componentTask.value?.nonArchivedRelatedTasks;
    if (!relatedTasks?.length) return true;
    return !relatedTasks.some(
      (task) => task.type === 'auto_response_email' || task.type === 'call_analysis',
    );
  });

  const emailThreadId = ref<string | undefined>(componentTask.value?.emailData?.thread_id);

  const emailFrom = computed((): EmailSender | undefined => {
    return emailSenders.value?.find((sender) => sender?.email === profile.value?.email);
  });

  const getQueryObjs = (arr, { prefix = '', type = '' } = {}) => {
    return arr?.map((text, index) => {
      const id = [prefix, type, index].join('_');
      return { id, text, type };
    });
  };

  let iframeCleanups: (() => void)[] = [];

  const markElements = (els: ArrayLike<Element>) => {
    const queries = evidenceQueries.value;
    if (!queries?.length) return;

    Array.from(els).forEach((el) => {
      const instance = new Mark(el as HTMLElement);
      instance.unmark({
        done: () => {
          queries.forEach((query: EvidenceQuery) => {
            instance.mark(query?.text ?? query, {
              acrossElements: true,
              className: `bg-highlight-muted ${query?.id}`,
              ignorePunctuation: [',', ',', "'", '"'],
              separateWordSearch: false,
            });
          });
        },
      });
    });
  };

  const highlightText = (id?: string) => {
    const containerId = id || emailId.value;
    if (!containerId) return;
    const container = document.getElementById(containerId);
    if (!container) return;

    iframeCleanups.forEach((fn) => fn());
    iframeCleanups = [];

    markElements(container.querySelectorAll('.prose:not(iframe)'));

    container.querySelectorAll('iframe.prose').forEach((iframe) => {
      const el = iframe as HTMLIFrameElement;
      const markIframe = () => {
        const doc = el.contentDocument;
        if (!doc?.body) return;
        if (!doc.getElementById('evidence-highlight-style')) {
          const style = doc.createElement('style');
          style.id = 'evidence-highlight-style';
          style.textContent = 'mark.bg-highlight-muted { background-color: #fef08a; }';
          doc.head.appendChild(style);
        }
        markElements([doc.body]);
      };
      markIframe();
      el.addEventListener('load', markIframe);
      iframeCleanups.push(() => el.removeEventListener('load', markIframe));
    });
  };

  const openConfirmDialog = () => {
    if (formDataRef.value?.isGenerating) {
      toast.error('Please wait for the response to finish generating before sending.');
      return;
    }
    showConfirmDialog.value = true;
  };

  const closeConfirmDialog = () => {
    showConfirmDialog.value = false;
  };

  const openDismissAlert = (actionType?: string) => {
    showDismissAlert.value = true;
    dismissAlertActionType.value = actionType;
  };

  const closeDismissAlert = () => {
    showDismissAlert.value = false;
    dismissAlertActionType.value = undefined;
  };

  const animationDir = computed(() => {
    switch (taskCollectionStore?.navigationDirection) {
      case 'backward':
        return 'task-content-backward';
      case 'forward':
        return 'task-content-forward';
      default:
        return '';
    }
  });

  const handleAcknowledge = async () => {
    if (!componentTask.value) return;

    isSubmitting.value = true;

    try {
      const response = await stuut.tasks.acknowledge(componentTask.value as Task);

      toast.success('Acknowledged task and marked complete.');

      afterTaskSuccess();
      return response;
    } catch (err: any) {
      toast.error(err.clientMessage || 'An error occurred');
    } finally {
      isSubmitting.value = false;
    }
  };

  const acknowledgeGroupTask = async () => {
    if (!componentTask.value || !componentTask.value?.isExpectsAcknowledge) {
      return;
    }

    isSubmitting.value = true;

    try {
      const response = await stuut.tasks.acknowledge(componentTask?.value);

      toast.success('Acknowledged all payment promises.');

      afterTaskSuccess();
      return response;
    } catch (err: any) {
      toast.error(err.clientMessage || 'An error occurred');
    } finally {
      isSubmitting.value = false;
    }
  };

  const completeAllSubtasks = async () => {
    if (!componentTask.value || !componentTask.value?.isExpectsComplete) {
      return;
    }

    isSubmitting.value = true;

    try {
      const promises: Promise<Task | undefined>[] = [];

      componentTask.value?.openActiveSubtasks?.forEach((task: Task) => {
        if (task?.canCompletePromiseSubtask) {
          promises.push(stuut.tasks.complete(task as Task));
        }
      });

      const response = await Promise.all(promises);

      toast.success('Completed all payment promises.');

      afterTaskSuccess();
      return response;
    } catch (err: any) {
      toast.error(err.clientMessage || 'An error occurred');
    } finally {
      isSubmitting.value = false;
    }
  };

  const handleConfirmClick = async () => {
    if (!componentTask.value) return;

    isSubmitting.value = true;

    try {
      // Handle SMS sending
      if (componentTask.value.isSmsSource) {
        const smsBody = (TaskSmsResponseRef.value?.body ?? '').trim();
        const sms = componentTask.value.smsData;

        if (!smsBody) {
          toast.error('Please enter a message');
          isSubmitting.value = false;
          return;
        }

        if (!sms?.from_number || !sms?.to_number) {
          toast.error('SMS phone numbers not available');
          isSubmitting.value = false;
          return;
        }

        await stuut.tasks.completeWithAction(componentTask.value, {
          body: smsBody,
          from_number: sms.to_number,
          to_number: sms.from_number,
        });

        toast.success('SMS sent successfully');
        afterTaskSuccess();
        closeConfirmDialog();
        return;
      }

      // Handle email sending
      const result = await formDataRef.value?.formData.validate();

      if (!result.valid) {
        const invalidFields = Object.keys(result.results)
          .filter((key) => result.results[key].valid === false)
          .map((field) => changeCase.capitalCase(field).split(' ')[0]);

        toast.error(
          `Could not send email. Fix these fields and retry: ${invalidFields.join(', ')}`,
        );

        return;
      }

      const response = await stuut.tasks.sendNewResponse(
        componentTask.value,
        {
          ...formDataRef.value?.formData.values,
          from_: formDataRef.value?.formData.values.from,
        },
        componentTask.value?.responseData as ResponseTaskData,
      );

      toast.success('Email response approved.');

      afterTaskSuccess();
      closeConfirmDialog();

      return response;
    } catch (err: any) {
      toast.error(err.clientMessage || 'An error occurred');
    } finally {
      isSubmitting.value = false;
    }
  };

  const resolveRequirement = async (req: TaskRequirement, data: Record<string, any>) => {
    try {
      await stuut.taskRequirements.resolve(req, data);
    } catch (err: any) {
      toast.error(err?.clientMessage || 'Failed to resolve requirement');
    }
  };

  const onRequirementSelectOption = (req: TaskRequirement, option: { key: string }) =>
    resolveRequirement(req, { resolution: { value: option.key } });

  const onRequirementResolve = (req: TaskRequirement, value: string) =>
    resolveRequirement(req, { resolution: { value } });

  const onRequirementApprove = (req: TaskRequirement) =>
    resolveRequirement(req, { resolution: { value: true } });

  const onRequirementReject = (req: TaskRequirement) =>
    resolveRequirement(req, { resolution: { value: false } });

  const onRequirementDismiss = async (req: TaskRequirement) => {
    try {
      await stuut.taskRequirements.dismiss(req);
    } catch (err: any) {
      toast.error(err?.clientMessage || 'Failed to dismiss requirement');
    }
  };

  const EmailResponseScrollRef = useTemplateRef('EmailResponseScrollRef');
  const REGENERATE_POLL_INTERVAL = 2500;

  const scrollResponseToBottom = async () => {
    await nextTick();
    EmailResponseScrollRef.value?.scrollTo({ x: 0, y: 'bottom' });
  };

  const taskHitlEnabled = computed(() => featureFlagsStore.flags['task-hitl']);

  const {
    isActive: isRegenerating,
    resume: startRegeneratePoll,
    pause: stopRegeneratePoll,
  } = useTimeoutPoll(
    async () => {
      const task = componentTask.value;

      if (!task?.id) {
        stopRegeneratePoll();
        return;
      }

      await stuut.tasks.retrieve(task.id, task);

      if (!task.isProcessing) {
        stopRegeneratePoll();
        await scrollResponseToBottom();
        formDataRef.value?.updateEmail(task.responseData?.data);
      }
    },
    REGENERATE_POLL_INTERVAL,
    { immediate: false },
  );

  onBeforeUnmount(() => {
    iframeCleanups.forEach((fn) => fn());
    iframeCleanups = [];
    stopRegeneratePoll();
  });

  const regenerateTask = async () => {
    if (!componentTask.value?.id || isRegenerating.value) return;

    try {
      await stuut.tasks.regenerate(componentTask.value.id, componentTask.value);
      await scrollResponseToBottom();
      startRegeneratePoll();
    } catch (err: any) {
      toast.error(err?.clientMessage || 'Failed to regenerate');
    }
  };

  const loadEmail = async () => {
    loadingEmail.value = true;

    try {
      if (emailId.value) {
        email.value = (await taskStore.peekFindEmail(emailId.value)) || null;
      }

      if (emailThreadId.value) {
        emailThread.value = await taskStore.peekFindEmailThread(emailThreadId.value);

        const participantEmails = emailThread.value?.getParticipants();

        validSenders.value = emailSenders.value?.filter(
          (sender) =>
            participantEmails?.includes(sender.email) || email.value?.hasRemoteLink(sender),
        );
      } else {
        validSenders.value = emailSenders.value;
      }

      if (callId.value) {
        call.value = await taskStore.peekFindCall(callId.value);
      }

      nextTick(async () => {
        highlightText();
      });
    } catch (error) {
      console.error(error);
    } finally {
      loadingEmail.value = false;
    }
  };

  const setInitialActiveToggle = () => {
    if (hasContext.value) return;

    if (componentTask.value?.invoices?.length) {
      sidebarView.value = 'invoices';
      return;
    }

    if (componentTask.value?.nonArchivedRelatedTasks?.length) {
      sidebarView.value = 'tasks';
    }
  };

  onBeforeMount(async () => {
    await loadEmail();

    // run async
    featureFlagsStore.loadFeatureFlags();

    evidenceQueries.value = [
      ...(getQueryObjs(componentTask.value?.classificationCallouts, {
        prefix: componentTask.value?.callData?.id,
        type: 'callout',
      }) || []),
      ...(getQueryObjs(componentTask.value?.classificationKeywords, {
        prefix: componentTask.value?.callData?.id,
        type: 'keyword',
      }) || []),
    ];

    nextTick(() => {
      highlightText();
    });

    if (partnerId.value) {
      partner.value = await taskStore.peekFindPartner(partnerId.value);
    }

    await emailSenderStore.fetchEmailSenders();
    setInitialActiveToggle();
  });

  const toggleEditMode = () => {
    if (editMode.value) {
      editModeOff();
    } else {
      editModeOn();
    }
  };

  const editModeOff = () => {
    editMode.value = false;

    PartnerOverviewRef.value?.open();
    nextTick(() => highlightText());
  };

  const editModeOn = () => {
    editMode.value = true;

    PartnerOverviewRef.value?.close();
  };

  const afterTaskSuccess = async () => {
    taskCollectionStore?.refreshAllTaskTables();

    if (isDisputeRoute.value) {
      await disputeStore?.updateDisputePage();
      return nextTick(() => taskCollectionStore?.launchConfetti());
    }

    if (componentTask.value?.isAnyDispute) {
      taskCollectionStore?.openAfterCompleteOverlay();
    } else {
      taskCollectionStore?.afterCompleteTask();
    }
  };

  const originalResponse = computed(() => componentTask.value?.responseData?.data?.html_body ?? '');

  const formDataResponse = computed(() => formDataRef.value?.formData.values.body ?? '');

  const isDisputeRoute = computed(
    () => typeof route.name === 'string' && route.name.includes('disputes.dispute'),
  );

  const isResponseDirty = computed(
    () => stripHTML(originalResponse.value) !== stripHTML(formDataResponse.value),
  );

  const isSenderParticipant = computed(() => {
    const senders = emailSenders.value?.map((sender) => sender.email);
    const taskDataFrom = currentTask?.value?.responseData?.data?.from_;

    return taskDataFrom && senders?.includes(taskDataFrom);
  });

  const submitting = ref(false);

  const openContactEditor = () => {
    TaskDisableContactRef.value?.openContactEditor?.();
  };

  const completeTask = async () => {
    if (!currentTask?.value) return;

    isSubmitting.value = true;

    try {
      isSubmitting.value = true;

      const response = await stuut.tasks.complete(currentTask?.value as Task, {
        close_reason: 'Mark as complete',
      });

      toast.success('Marked as complete');

      afterTaskSuccess();
      return response;
    } catch (err: any) {
      toast.error(err.clientMessage || 'An error occurred');
    } finally {
      isSubmitting.value = false;
    }
  };

  const handleCompleteTask = async () => {
    if (!currentTask?.value || currentTask.value?.hasUploadingAttachments) return;

    if (currentTask?.value?.isDisableContact) {
      if (
        (currentTask?.value?.isEmailSource && !partner.value?.primaryEmail) ||
        (currentTask?.value?.isCallSource && !partner.value?.primaryPhone)
      ) {
        showCompleteWarning.value = true;
        return;
      }
    }

    return completeTask();
  };

  const bulkComplete = async (
    actionType?: 'acknowledge' | 'complete' | 'complete_with_action',
    taskType?: string,
  ) => {
    if (!actionType) return;

    isBulkCompleting.value = true;

    try {
      const response = await stuut.tasks.bulkCompleteAction(
        TaskTableDrawerRef?.value?.selectedItems?.map((task) => task.id),
        actionType,
        taskType || undefined,
        TaskTableDrawerRef?.value?.selectedItems,
      );

      toast.success(`Processing ${TaskTableDrawerRef?.value?.selectedItems?.length} tasks`);

      afterTaskSuccess();
      return response;
    } catch (err: any) {
      toast.error(err?.clientMessage || 'An error occurred');
    } finally {
      isBulkCompleting.value = false;
    }
  };

  const isCompactTaskType = computed(
    () =>
      componentTask?.value?.isEscalation ||
      componentTask?.value?.isManualCall ||
      componentTask?.value?.isPauseStateUpdate,
  );

  const breakpoints = useBreakpoints(breakpointsTailwind);
  const isLgUp = breakpoints.greaterOrEqual('lg');
  const useHorizontalSplitter = computed(() => isLgUp.value && !isCompactTaskType.value);

  const isNewEmail = computed(
    () =>
      !email.value?.id &&
      (componentTask.value?.isCallSource || componentTask.value?.isBrokenPromiseFollowUp),
  );

  const canReplyWithoutThreadParticipation = computed(
    () => isNewEmail.value || !componentTask.value?.responseData?.data?.from_,
  );

  const handleDisputeTask = async () => {
    if (submitting.value || !componentTask.value) return;

    try {
      submitting.value = true;

      const response = await disputeStore?.completeDisputeTask(
        taskDisputeFormRef.value?.formData,
        componentTask.value,
      );

      afterTaskSuccess();

      return response;
    } catch (err: any) {
      toast.error(err.clientMessage || err.message || err);
    } finally {
      submitting.value = false;
    }
  };

  const resolveDisputeTask = async () => {
    if (submitting.value || !componentTask.value) return;

    try {
      submitting.value = true;

      const response = await stuut.tasks.completeWithAction(componentTask.value);

      afterTaskSuccess();

      return response;
    } catch (err: any) {
      toast.error(err.clientMessage || err.message || err);
    } finally {
      submitting.value = false;
    }
  };

  defineExpose({
    editMode,
    editModeOff,
    isDirty: isResponseDirty,
    partner,
    TaskTableDrawerRef,
  });
</script>

<template>
  <div
    class="flex flex-1 flex-col overflow-auto relative z-10"
    :class="{
      rounded: !editMode,
      'lg:overflow-hidden': !isCompactTaskType,
    }"
    style="transition: width 250ms cubic-bezier(0.4, 0, 0.2, 1)"
  >
    <component
      :is="useHorizontalSplitter ? ResizablePanelGroup : 'div'"
      v-bind="useHorizontalSplitter ? { direction: 'horizontal' } : {}"
      :class="
        useHorizontalSplitter
          ? { rounded: !editMode, 'lg:overflow-hidden': true }
          : {
              'flex flex-col h-auto flex-1': true,
              rounded: !editMode,
              'lg:flex-row lg:overflow-hidden': !isCompactTaskType,
            }
      "
    >
      <component
        :is="useHorizontalSplitter ? ResizablePanel : 'div'"
        v-bind="
          useHorizontalSplitter
            ? { id: 'task-email-response-main', order: 1, defaultSize: 75, minSize: 5 }
            : {}
        "
        :class="useHorizontalSplitter ? undefined : 'contents'"
      >
        <ScrollArea
          ref="EmailResponseScrollRef"
          class="[&>div]:outline-transparent @container [&>div]:outline-none border-l min-w-86"
          :disabled="editMode"
          :class="{
            'px-4': !editMode,
            'flex-1': !isCompactTaskType && !useHorizontalSplitter,
            'h-full w-full': useHorizontalSplitter,
          }"
        >
          <TaskCompleted
            v-if="!isTaskListMode && !componentTask?.isOpen"
            class="mt-5"
            :task="currentTask"
          />

          <template v-if="isTaskListMode">
            <div class="py-4">
              <Transition name="table-fade" mode="out-in" appear>
                <PaginatedTaskDrawerTable
                  ref="TaskTableDrawerRef"
                  :key="`${route.query.group_view_id}${route.query.collection_type}${route.query.collection_id}${route.query.sub_collection_type}${route.query.sub_collection_id}${partner?.id}`"
                  :class="cn('h-fit', !componentTask?.isOpen && !editMode && 'opacity-60')"
                  :partner="undefined"
                />
              </Transition>
            </div>
          </template>

          <template v-else>
            <div
              v-if="taskCollectionStore?.isLoadingTask"
              class="flex-1 bg-secondary space-y-8 py-4"
              :class="{
                'opacity-60': !componentTask?.isOpen && !editMode,
              }"
            >
              <Skeleton class="w-full h-5 flex-1 rounded" />

              <div
                class="text-xs flex flex-nowrap items-center w-full h-40 place-content-center gap-x-4 relative"
              >
                <Skeleton class="h-40 flex-1 rounded" />

                <div
                  class="absolute text-xs flex flex-nowrap items-center w-full place-content-center gap-x-4"
                >
                  <!-- <StuutLoading /> -->

                  <div class="space-y-1">
                    <strong>Hang tight!</strong>
                    <div class="text-muted-foreground">Loading your task...</div>
                  </div>
                </div>
              </div>

              <Skeleton class="w-20 h-5 flex-1 rounded" />

              <div class="flex-1 space-y-4">
                <Skeleton class="h-5 flex-1 rounded" />
                <Skeleton class="h-5 flex-1 rounded" />
                <Skeleton class="h-5 flex-1 rounded" />
              </div>
            </div>

            <Transition v-else :name="animationDir" mode="out-in" appear>
              <div
                :key="`task-content-${componentTask?.id}`"
                class="bg-background text-xs w-full space-y-8"
                :class="{
                  'py-4 pb-10': !editMode,
                  'h-full': editMode,
                }"
                data-section="task-details"
              >
                <Transition name="shrink">
                  <div v-if="!editMode" class="space-y-8">
                    <div class="space-y-4">
                      <TaskTitle
                        v-if="componentTask?.id"
                        class="truncate"
                        :task="componentTask"
                        :partner="partner as Partner"
                        :edit-mode="editMode"
                      >
                        <template #title>
                          <template v-if="componentTask?.customDescription?.title">
                            {{ componentTask?.customDescription?.title }}
                          </template>

                          <template v-else>
                            <template
                              v-if="
                                componentTask?.isPaymentPromise ||
                                componentTask?.isBrokenPromiseFollowUp
                              "
                            >
                              Payment Promised
                            </template>

                            <template v-else-if="componentTask?.isNewContact">
                              Review recommended contact
                            </template>

                            <template v-else-if="componentTask?.isDisableContact">
                              Contact
                              <span class="capitalize">
                                {{ componentTask?.source }}
                              </span>
                              Disabled
                            </template>

                            <template v-else-if="componentTask?.isEscalation">
                              Review manual task
                            </template>

                            <template v-else-if="isReplyRequired">
                              <strong class="text-sm truncate">
                                {{ componentTask.label }}
                              </strong>
                            </template>

                            <template v-else-if="TaskTypeMap[componentTask?.type as string]?.title">
                              {{ TaskTypeMap[componentTask?.type as string]?.title }}
                            </template>

                            <template v-else> Review task </template>
                          </template>
                        </template>
                      </TaskTitle>

                      <TaskBrief
                        v-if="!showBriefInSidebar"
                        :class="
                          cn(
                            'bg-secondary rounded-xs',
                            !editMode && 'p-5',
                            !componentTask?.isOpen && !editMode && 'opacity-40',
                          )
                        "
                        :task="componentTask as Task"
                        :group-id="currentTask?.group_id ?? undefined"
                        @open-contact-editor="openContactEditor"
                      />

                      <div
                        v-if="
                          (taskHitlEnabled && taskRequirements?.length) ||
                          $slots.summary_after_reason
                        "
                        :class="
                          cn(
                            'bg-muted rounded-xs',
                            !editMode && 'p-5',
                            !componentTask?.isOpen && !editMode && 'opacity-40',
                          )
                        "
                      >
                        <div class="flex flex-col gap-y-4">
                          <strong>Agent will use the following to draft your response:</strong>

                          <TaskRequirements
                            v-if="componentTask && !componentTask.is_group"
                            :task="componentTask"
                            :requirements="taskRequirements"
                            :partner-id="partnerId ?? undefined"
                            @select-option="onRequirementSelectOption"
                            @resolve="onRequirementResolve"
                            @approve="onRequirementApprove"
                            @reject="onRequirementReject"
                            @dismiss="onRequirementDismiss"
                          />

                          <slot name="summary_after_reason" />
                        </div>
                      </div>

                      <div
                        v-if="taskHitlEnabled && taskRequirements?.length"
                        class="flex flex-col text-muted-foreground gap-y-2 ml-0"
                      >
                        <Button
                          variant="onyx"
                          size="sm"
                          class="w-fit"
                          :disabled="
                            !componentTask?.isOpen ||
                            isRegenerating ||
                            !taskRequirements.some((req) => req.isAnswered)
                          "
                          @click="regenerateTask"
                        >
                          <i
                            v-if="isRegenerating"
                            class="fa-regular fa-spinner text-xs animate-spin"
                          ></i>
                          <i v-else class="far fa-sparkle text-xs"></i>
                          <span>Regenerate Email</span>
                        </Button>

                        <div class="flex items-center gap-x-1">
                          <Tooltip v-if="hasRequirementsModifiedAfterTask" :delay-duration="0">
                            <TooltipTrigger as-child>
                              <i class="fas fa-triangle-exclamation text-warning text-2xs" />
                            </TooltipTrigger>

                            <TooltipContent class="w-90">
                              Requirements have been updated since this draft was generated —
                              regenerate to apply the latest input.
                            </TooltipContent>
                          </Tooltip>

                          <span class="text-2xs">Last generated at:</span>

                          <Timestamp
                            :time="
                              componentTask?.responseData?.created_at
                                ? dayjs.utc(componentTask.responseData.created_at)
                                : undefined
                            "
                            format="MMM D, h:mmA"
                            :show-from-now="true"
                            :local="true"
                            class="!text-2xs text-muted-foreground tabular-nums shrink-0 w-fit"
                          />
                        </div>
                      </div>

                      <DisputeUpsellCard
                        v-if="
                          profileStore?.isBaseDisputesEnabled &&
                          !isDisputesPro &&
                          componentTask?.isDisputeCreate
                        "
                        :compact="true"
                      />

                      <template v-else>
                        <Alert
                          v-if="componentTask?.isDisputeCreate"
                          icon="far fa-balance-scale"
                          size="xs"
                          variant="warning"
                        >
                          <AlertDescription v-if="isDisputesPro">
                            Stuut detected a potential dispute but cannot confirm it due to limited
                            information. Please review the details, determine whether it is a
                            dispute, and complete the required fields.
                          </AlertDescription>
                          <AlertDescription v-else>
                            Upgrade to Disputes Pro to let the agent automatically match to your
                            reason codes and fill in the required information.
                          </AlertDescription>
                        </Alert>

                        <template v-if="componentTask?.isDisputeUpdate">
                          <Alert icon="far fa-balance-scale" size="xs" variant="warning">
                            <AlertDescription>
                              We've detected updates related to this dispute. Please review the
                              changes, make any necessary edits, and then accept or reject them to
                              update the case.
                            </AlertDescription>
                          </Alert>

                          <Alert
                            v-if="componentTask?.dispute?.isTerminal"
                            icon="far fa-balance-scale"
                            size="xs"
                            variant="destructive"
                          >
                            <AlertDescription>
                              Dispute
                              <strong>#{{ componentTask?.dispute?.number }}</strong>
                              is currently
                              <strong>{{ componentTask?.dispute?.status }}</strong
                              >.
                            </AlertDescription>
                          </Alert>
                        </template>
                      </template>
                    </div>

                    <TaskPauseStateUpdateView
                      v-if="componentTask?.isPauseStateUpdate"
                      :class="{
                        'opacity-40': !componentTask?.isOpen && !editMode,
                      }"
                    />

                    <TaskDisputeForm
                      v-if="componentTask?.isDisputeAnyCreate || componentTask?.isDisputeUpdate"
                      ref="TaskDisputeFormRef"
                      :class="{
                        'opacity-80': !componentTask?.isOpen && !editMode,
                      }"
                      :task="componentTask"
                      :disabled="!componentTask.isOpen"
                    />

                    <TaskResolution
                      v-if="componentTask?.isDisputeResolution"
                      :class="{
                        'opacity-80': !componentTask?.isOpen && !editMode,
                      }"
                      :task="componentTask"
                    />

                    <TaskNewContact
                      v-if="componentTask?.isNewContact"
                      :key="`${componentTask?.id}-new-contact`"
                      ref="TaskNewContactRef"
                      :class="{
                        'opacity-40': !componentTask?.isOpen && !editMode,
                      }"
                    />

                    <TaskDisableContactView
                      v-if="componentTask?.isDisableContact"
                      ref="TaskDisableContactRef"
                    />

                    <PaymentPromiseSubtasks
                      v-if="componentTask?.isPaymentPromiseGroup"
                      ref="PaymentPromiseSubtasksRef"
                    />

                    <TaskBrokenPromisedPayments
                      v-if="componentTask?.isBrokenPromiseFollowUp"
                      :class="{
                        'opacity-40': !componentTask?.isOpen && !editMode,
                      }"
                      :task="componentTask"
                      :partner="partner as Partner"
                    />

                    <TaskPromisedPayments
                      v-if="
                        !componentTask?.isBrokenPromiseFollowUp &&
                        (componentTask?.promisesData?.length || componentTask?.payment_promise?.id)
                      "
                      :task="componentTask"
                      :partner="partner as Partner"
                    />
                  </div>
                </Transition>

                <div
                  v-if="isReplyRequired"
                  class="transition-all origin-center space-y-4"
                  :class="{
                    'opacity-40': !componentTask?.isOpen && !editMode,
                    'h-full border-transparent': editMode,
                  }"
                >
                  <div v-if="!editMode" class="flex items-center justify-between gap-x-2">
                    <div class="flex items-center gap-x-2">
                      <strong>
                        <template v-if="componentTask?.isCompleted">
                          Sent

                          <span
                            v-if="componentTask?.completed_action?.user?.name"
                            class="font-normal"
                          >
                            by {{ componentTask?.completed_action?.user?.name }}
                          </span>
                        </template>

                        <template v-else-if="componentTask?.isProcessing">Sending</template>

                        <template v-else>Drafted by agent</template>
                      </strong>
                    </div>
                  </div>

                  <div class="bg-background" :class="editMode && 'h-full'">
                    <TaskSmsResponse
                      v-if="componentTask?.isSmsSource"
                      ref="TaskSmsResponseRef"
                      :sms="componentTask?.smsData"
                      :drafted-body="componentTask?.responseData?.data?.body"
                      :disabled="!componentTask?.isOpen"
                      class="p-4"
                    />

                    <EmailAutoResponse
                      v-else-if="isReplyRequired && !componentTask?.isSmsSource"
                      ref="formDataRef"
                      :task="componentTask"
                      :task-data="componentTask?.responseData!"
                      :is-completed="componentTask?.isDone"
                      :completed-action="componentTask?.completed_action!"
                      :disable-editing="!componentTask?.isOpen"
                      :email-id="email?.id"
                      :email-sender-id="emailFrom?.id"
                      :partner="partner as Partner"
                      :valid-senders="validSenders"
                      :show-subject="
                        componentTask?.isCallAnalysis || componentTask?.isBrokenPromiseFollowUp
                      "
                      class="email-auto-response flex w-full h-full whitespace-pre-wrap [&>div]:w-full"
                      :class="editMode ? '' : 'max-h-130'"
                      :ignore-disabled-effect="
                        editMode || Boolean(componentTask?.completed_action!)
                      "
                      :is-new-email="isNewEmail"
                      :sticky-toolbar="editMode"
                      :edit-mode="editMode"
                      @back-to-draft="editModeOff"
                      @click="
                        (e) => {
                          if (
                            !editMode &&
                            formDataRef.container?.contains(e.target) &&
                            componentTask?.isOpen
                          )
                            toggleEditMode();
                        }
                      "
                    >
                    </EmailAutoResponse>
                  </div>
                </div>
              </div>
            </Transition>

            <TaskThinking
              v-if="showThinking"
              :is-processing="!!componentTask?.isProcessing"
              class="py-10 absolute top-0"
              @done="onThinkingDone"
            />

            <div
              v-if="!showBriefInSidebar && hasContext && !editMode"
              class="space-y-4 pt-4"
              :class="!showBriefInSidebar && 'pb-5'"
            >
              <TaskEmailContext
                v-if="componentTask?.isEmailSource"
                :email="componentTask?.email as Email"
                :email-thread="componentTask?.email_thread as EmailThread"
                :show-reply-button="showPaymentPromiseReply"
              />

              <TaskCallContext v-else-if="componentTask?.isCallSource" :task="componentTask" />

              <TaskSmsContext
                v-else-if="componentTask?.isSmsSource"
                :sms="componentTask?.smsData"
              />
            </div>
          </template>
        </ScrollArea>
      </component>

      <ResizableHandle
        v-if="useHorizontalSplitter && showBriefInSidebar && !isTaskListMode"
        with-handle
        class="items-start pt-[300px]"
      />

      <component
        :is="useHorizontalSplitter ? ResizablePanel : 'div'"
        v-if="showBriefInSidebar && !isTaskListMode"
        v-bind="
          useHorizontalSplitter
            ? {
                id: 'task-email-response-sidebar',
                order: 2,
                defaultSize: 50,
                minSize: 5,
              }
            : {}
        "
        :class="useHorizontalSplitter ? undefined : 'contents'"
      >
        <ScrollArea
          class="border-l min-w-83"
          :class="
            useHorizontalSplitter
              ? 'h-full w-full'
              : ['w-full shrink-0', { 'lg:w-90 xl:w-100 2xl:w-110': !isCompactTaskType }]
          "
        >
          <div class="flex flex-col px-4 pb-4 gap-y-4" :class="isCompactTaskType ? '' : 'pt-4'">
            <TaskBrief
              class="bg-secondary p-5 rounded-xs"
              :task="componentTask as Task"
              :group-id="currentTask?.group_id ?? undefined"
              @open-contact-editor="openContactEditor"
              @select-related-tasks="sidebarView = 'tasks'"
            />

            <ToggleGroup
              :model-value="sidebarView"
              type="single"
              variant="outline"
              size="sm"
              class="w-full"
              @update:model-value="
                (v) => v && (sidebarView = v as 'context' | 'invoices' | 'tasks')
              "
            >
              <ToggleGroupItem
                value="context"
                aria-label="Context"
                :disabled="!hasContext"
                class="flex-1 text-muted-foreground data-[state=on]:bg-secondary data-[state=on]:text-foreground"
              >
                <Tooltip>
                  <TooltipTrigger as-child>
                    <i class="far fa-messages" />
                  </TooltipTrigger>
                  <TooltipContent>Related Communications</TooltipContent>
                </Tooltip>
              </ToggleGroupItem>

              <ToggleGroupItem
                :disabled="!componentTask?.invoices?.length"
                value="invoices"
                aria-label="Invoices"
                class="flex-1 text-muted-foreground data-[state=on]:bg-secondary data-[state=on]:text-foreground"
              >
                <Tooltip>
                  <TooltipTrigger as-child>
                    <i class="far fa-file-invoice-dollar" />
                  </TooltipTrigger>
                  <TooltipContent>Related Invoices</TooltipContent>
                </Tooltip>
              </ToggleGroupItem>

              <ToggleGroupItem
                value="tasks"
                aria-label="Tasks"
                :disabled="!componentTask?.nonArchivedRelatedTasks?.length"
                class="flex-1 text-muted-foreground data-[state=on]:bg-secondary data-[state=on]:text-foreground"
              >
                <Tooltip>
                  <TooltipTrigger as-child>
                    <div class="flex items-center gap-x-3">
                      <div class="relative">
                        <i class="far fa-clipboard-check" />
                        <span
                          class="absolute -top-0.5 -right-1.5 size-1.5 rounded-full"
                          :class="
                            componentTask?.nonArchivedRelatedTasks?.some((tsk) =>
                              ['urgent', 'high'].includes(tsk.priority),
                            )
                              ? 'bg-destructive'
                              : 'bg-primary'
                          "
                        />
                      </div>

                      <strong class="text-2xs">{{
                        componentTask?.nonArchivedRelatedTasks?.length
                      }}</strong>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>Related Tasks</TooltipContent>
                </Tooltip>
              </ToggleGroupItem>
            </ToggleGroup>

            <div
              v-if="sidebarView === 'invoices' && componentTask?.invoices?.length"
              class="flex flex-col gap-y-4 w-full"
            >
              <InvoiceCompact
                v-for="invoice in componentTask.invoices"
                :key="invoice.id"
                :invoice="invoice"
                :customer="componentTask.partner"
                :show-customer="false"
              />
            </div>

            <div
              v-else-if="sidebarView === 'tasks' && componentTask?.nonArchivedRelatedTasks"
              class="flex flex-col gap-y-4 w-full"
            >
              <TaskCompact
                v-for="relatedTask in componentTask?.nonArchivedRelatedTasks"
                :key="relatedTask.id"
                :task="relatedTask"
              />
            </div>

            <template v-else>
              <TaskEmailContext
                v-if="componentTask?.isEmailSource"
                :email="componentTask?.email as Email"
                :email-thread="componentTask?.email_thread as EmailThread"
                :show-reply-button="showPaymentPromiseReply"
              />

              <TaskCallContext v-else-if="componentTask?.isCallSource" :task="componentTask" />

              <TaskSmsContext
                v-else-if="componentTask?.isSmsSource"
                :sms="componentTask?.smsData"
              />
            </template>
          </div>
        </ScrollArea>
      </component>
    </component>

    <div
      class="bg-background flex flex-nowrap items-center justify-between py-2 border-t h-14 sticky bottom-0"
      :class="{
        'pl-5 pr-10': route.query.group_view_mode === 'partner',
        'px-5': route.query.group_view_mode !== 'partner',
      }"
      data-section="task-footer"
    >
      <div class="flex items-center gap-x-2.5 flex-1">
        <template v-if="isTaskListMode">
          <Button
            v-if="
              !profile?.isEmailSenderSetup &&
              isReplyRequired &&
              !componentTask?.isSmsSource &&
              componentTask?.isOpen
            "
            variant="agent"
            size="sm"
            @click="linkEmailDialogRef.open()"
          >
            Link Email To Respond
          </Button>

          <ConfirmDialog
            v-if="
              currentModalTaskType === 'auto_response_email' ||
              currentModalTaskType === 'broken_promise_follow_up' ||
              currentModalTaskType === 'call_analysis' ||
              (currentModalTaskType === 'payment_promise' &&
                taskCollectionStore?.currentTask?.isReplyRequired)
            "
            @confirm="bulkComplete('complete_with_action', 'auto_response_email')"
          >
            <template #title>
              Send
              {{ TaskTableDrawerRef?.selectedItems?.length }}
              {{ pluralize('email', TaskTableDrawerRef?.selectedItems?.length) }}?
            </template>
            <template #description> This action cannot be undone. Please confirm. </template>

            <template #trigger>
              <Button
                :disabled="TaskTableDrawerRef?.selectedItems <= 0"
                :loading="isBulkCompleting"
                confirm-text="Send"
                size="sm"
              >
                <i class="far fa-paper-plane"></i>
                <span>Bulk send</span>
              </Button>
            </template>
          </ConfirmDialog>

          <ConfirmDialog
            confirm-text="Acknowledge"
            @confirm="
              bulkComplete(
                taskCollectionStore?.selectedSubCollection?.expectedAction,
                taskCollectionStore?.selectedCollectionId,
              )
            "
          >
            <template #title>
              Acknowledge
              {{ TaskTableDrawerRef?.selectedItems?.length }}
              {{ pluralize('task', TaskTableDrawerRef?.selectedItems?.length) }}?
            </template>
            <template #description> Please confirm. </template>

            <template #trigger>
              <Button
                v-if="taskCollectionStore?.selectedSubCollection?.isExpectsAcknowledge"
                :disabled="
                  !componentTask?.isOpen ||
                  TaskTableDrawerRef?.selectedItems <= 0 ||
                  isBulkCompleting
                "
                size="sm"
              >
                <i class="fas fa-thumbs-up text-xs" />
                Acknowledge!
              </Button>
            </template>
          </ConfirmDialog>

          <TaskDrawerTablePagination
            v-if="TaskTableDrawerRef?.table"
            :table="TaskTableDrawerRef?.table"
            :global-selection="TaskTableDrawerRef?.selectedItems"
            :total-records="TaskTableDrawerRef?.totalRecords"
            @clear-selection="TaskTableDrawerRef?.clearSelectedItems"
          >
            <template #actions>
              <TasksToolbar
                :tasks="TaskTableDrawerRef?.selectedItems"
                button-text="Other actions"
                @clear-selection="TaskTableDrawerRef?.clearSelectedItems"
              />
            </template>
          </TaskDrawerTablePagination>
        </template>

        <template v-else-if="isTaskStackedMode">
          <template v-if="componentTask?.is_group">
            <Tooltip v-if="componentTask?.isExpectsComplete">
              <TooltipTrigger as-child>
                <Button
                  v-if="componentTask?.isExpectsComplete"
                  size="sm"
                  :disabled="
                    !componentTask?.isOpen || !PaymentPromiseSubtasksRef?.canCompleteAllSubtasks
                  "
                  :loading="isSubmitting"
                  @click="completeAllSubtasks"
                >
                  <i class="fas fa-check text-xs"></i>

                  <span>
                    Mark
                    {{ (componentTask?.activeSubtasks?.length || 0) > 1 ? ' All' : ' as' }}
                    complete
                  </span>
                </Button>
              </TooltipTrigger>

              <TooltipContent>
                All tasks must have a promise to pay date set to complete
              </TooltipContent>
            </Tooltip>

            <Button
              v-if="componentTask?.isExpectsAcknowledge"
              size="sm"
              :disabled="!componentTask?.isOpen"
              :loading="isSubmitting"
              @click="acknowledgeGroupTask"
            >
              <i class="fas fa-thumbs-up" />
              <span>
                Acknowledge{{ (componentTask?.activeSubtasks?.length || 0) > 1 ? ' All!' : '!' }}
              </span>
            </Button>

            <Tooltip :delay-duration="0">
              <TooltipTrigger as-child>
                <Button
                  size="sm"
                  variant="secondary"
                  :disabled="!componentTask?.isOpen || isSubmitting"
                  :loading="isSubmitting"
                  @click="openDismissAlert('close')"
                >
                  <span class="fa-stack">
                    <i class="fas fa-slash fa-stack-1x"></i>
                    <i class="fas fa-list-ul fa-stack-1x"></i>
                  </span>

                  <span>Dismiss All</span>
                </Button>
              </TooltipTrigger>

              <TooltipContent> Close all subtasks </TooltipContent>
            </Tooltip>
          </template>

          <template v-else>
            <Button
              v-if="
                !profile.isEmailSenderSetup &&
                isReplyRequired &&
                !componentTask?.isSmsSource &&
                componentTask?.isOpen
              "
              variant="agent"
              :disabled="isSubmitting"
              size="sm"
              @click="linkEmailDialogRef.open()"
            >
              Link Email To Respond
            </Button>

            <template v-if="isReplyRequired">
              <template
                v-if="taskHitlEnabled && (componentTask?.isOpen || componentTask?.isProcessing)"
              >
                <Tooltip :disabled="Boolean(isSenderParticipant)">
                  <TooltipTrigger as-child>
                    <Button
                      size="sm"
                      variant="onyx"
                      :disabled="
                        !componentTask?.isOpen ||
                        (!canReplyWithoutThreadParticipation && !isSenderParticipant) ||
                        componentTask?.isProcessing ||
                        isSubmitting ||
                        isResponseGenerating ||
                        componentTask?.hasUploadingAttachments
                      "
                      @click.stop="openConfirmDialog"
                    >
                      <template v-if="isSubmitting">
                        <i class="fa-regular fa-spinner text-xs animate-spin"></i>Sending...
                      </template>

                      <template v-else>
                        <i class="far fa-paper-plane"></i>
                        Send
                      </template>
                    </Button>
                  </TooltipTrigger>

                  <TooltipContent>
                    <template v-if="!canReplyWithoutThreadParticipation && !isSenderParticipant">
                      You cannot respond because you are not a participant on this thread.
                    </template>
                  </TooltipContent>
                </Tooltip>

                <Tooltip :delay-duration="0">
                  <TooltipTrigger as-child>
                    <Button
                      size="sm"
                      :disabled="componentTask?.isProcessing || isSubmitting"
                      :variant="editMode ? 'ghost' : 'outline'"
                      @click="toggleEditMode"
                    >
                      <template v-if="editMode">
                        <i class="far fa-arrow-left"></i>
                        Exit edit mode
                      </template>

                      <template v-else>
                        <i class="far fa-pen-to-square"></i>
                        Edit Mode
                      </template>
                    </Button>
                  </TooltipTrigger>

                  <TooltipContent>
                    {{ editMode ? 'Exit full panel editor' : 'Open full panel editor' }}
                  </TooltipContent>
                </Tooltip>
              </template>

              <Tooltip
                v-else
                :disabled="Boolean(isSenderParticipant) || componentTask?.isSmsSource"
              >
                <TooltipTrigger as-child>
                  <Button
                    size="sm"
                    :disabled="
                      !componentTask?.isOpen ||
                      (!componentTask?.isSmsSource &&
                        !canReplyWithoutThreadParticipation &&
                        !isSenderParticipant) ||
                      isSubmitting ||
                      isResponseGenerating ||
                      componentTask?.hasUploadingAttachments
                    "
                    @click.stop.prevent="openConfirmDialog"
                  >
                    <template v-if="componentTask?.isProcessing">
                      <i class="fa-regular fa-spinner text-xs animate-spin"></i>Sending...
                    </template>

                    <template v-else>
                      <i class="far fa-paper-plane"></i>
                      Send
                    </template>
                  </Button>
                </TooltipTrigger>

                <TooltipContent>
                  <template v-if="!canReplyWithoutThreadParticipation && !isSenderParticipant">
                    You cannot respond because you are not a participant on this thread.
                  </template>
                </TooltipContent>
              </Tooltip>
            </template>

            <Button
              v-else-if="componentTask?.isNewContact"
              type="submit"
              size="sm"
              :disabled="!componentTask?.isOpen || isSubmitting"
              @click="TaskNewContactRef?.submit"
            >
              <template v-if="componentTask?.isProcessing || TaskNewContactRef?.isSubmitting">
                <i class="fa-regular fa-spinner text-xs animate-spin"></i>Adding contact...
              </template>

              <template v-else>Add contact</template>
            </Button>

            <Button
              v-else-if="componentTask?.isExpectsComplete"
              size="sm"
              :disabled="!componentTask?.isOpen"
              :loading="isSubmitting"
              @click="handleCompleteTask"
            >
              <i class="fas fa-check text-xs"></i>
              <span> Mark as complete </span>
            </Button>

            <Button
              v-else-if="componentTask?.isExpectsAcknowledge"
              size="sm"
              :disabled="!componentTask?.isOpen"
              :loading="isSubmitting"
              @click="handleAcknowledge"
            >
              <i class="fas fa-thumbs-up text-xs" />
              <span>Acknowledge!</span>
            </Button>

            <Button
              v-else-if="componentTask?.isDisputeAnyCreate || componentTask?.isDisputeUpdate"
              :disabled="!componentTask?.isOpen || isSubmitting"
              :loading="isSubmitting"
              size="sm"
              @click="handleDisputeTask"
            >
              <i class="fas fa-plus-circle"></i>
              <span>{{ TaskTypeMap[componentTask?.type as string]?.actionBtn }}</span>
            </Button>

            <Button
              v-else-if="componentTask?.isDisputeResolution"
              :disabled="!componentTask?.isOpen || isSubmitting"
              :loading="isSubmitting"
              size="sm"
              @click="resolveDisputeTask"
            >
              <i class="fas fa-plus-circle"></i>
              <span>{{ TaskTypeMap[componentTask?.type as string]?.actionBtn }}</span>
            </Button>

            <Button
              v-else-if="
                currentModalTaskType === 'payment_promise' ||
                currentModalTaskType === 'disable_contact' ||
                (currentModalTaskType === 'call_analysis' &&
                  !taskCollectionStore?.currentTask?.isReplyRequired) ||
                (taskCollectionStore?.currentTask?.isPaymentPromise &&
                  taskCollectionStore?.currentTask?.isActionRequired)
              "
              size="sm"
              :disabled="!componentTask?.isOpen || isSubmitting"
              :loading="isSubmitting"
              @click="handleAcknowledge"
            >
              <i class="fas fa-thumbs-up text-xs" />
              <span>Acknowledge!</span>
            </Button>

            <Button
              v-else-if="componentTask?.isEscalation"
              variant="warning"
              size="sm"
              :disabled="!componentTask?.isOpen"
              :loading="isSubmitting"
              @click="completeTask"
            >
              <i class="fas fa-check text-xs"></i>
              <span>Mark as complete</span>
            </Button>

            <Tooltip :delay-duration="0">
              <TooltipTrigger as-child>
                <Button
                  variant="secondary"
                  size="sm"
                  :disabled="!componentTask?.isOpen || isSubmitting"
                  :loading="isSubmitting"
                  @click="openDismissAlert('close')"
                >
                  <template v-if="TaskTypeMap[componentTask?.type as string]?.cancelIcon">
                    <i :class="TaskTypeMap[componentTask?.type as string]?.cancelIcon" class="fa" />
                  </template>

                  <span v-else class="fa-stack">
                    <i class="fa-solid fa-slash fa-stack-1x"></i>
                    <i class="fas fa-list-ul fa-stack-1x"></i>
                  </span>

                  <span>{{
                    TaskTypeMap[componentTask?.type as string]?.cancelBtn || 'Dismiss'
                  }}</span>
                </Button>
              </TooltipTrigger>

              <TooltipContent> Mark task as closed </TooltipContent>
            </Tooltip>
          </template>
        </template>
      </div>

      <div class="flex items-center gap-x-2.5">
        <template v-if="isTaskStackedMode">
          <template
            v-if="
              !taskHitlEnabled &&
              isReplyRequired &&
              (componentTask?.isOpen || componentTask?.isCompleted)
            "
          >
            <Tooltip v-if="editMode">
              <TooltipTrigger as-child>
                <Button
                  size="sm"
                  variant="outline"
                  :edge="false"
                  :disabled="componentTask?.isProcessing"
                  @click="toggleEditMode"
                >
                  <i class="far fa-arrow-left"></i>
                  View full task
                </Button>
              </TooltipTrigger>

              <TooltipContent> Exit full panel edit mode </TooltipContent>
            </Tooltip>

            <Tooltip v-else :delay-duration="0" :disabled="!componentTask?.isOpen">
              <TooltipTrigger as-child>
                <Button
                  size="sm"
                  variant="outline"
                  :edge="false"
                  :disabled="!(componentTask?.isOpen || componentTask?.isCompleted)"
                  @click="toggleEditMode"
                >
                  <template v-if="componentTask?.isOpen">
                    <i class="far fa-pen-to-square"></i>
                    Edit mode
                  </template>

                  <template v-else-if="componentTask?.isCompleted">
                    <i class="far fa-eye"></i>
                    View sent response
                  </template>

                  <template v-else>
                    <i class="far fa-eye"></i>
                    View response
                  </template>
                </Button>
              </TooltipTrigger>

              <TooltipContent> Full panel edit mode </TooltipContent>
            </Tooltip>
          </template>

          <RelatedTasks
            v-if="
              isTaskStackedMode &&
              componentTask?.nonArchivedRelatedTasks?.length &&
              !showBriefInSidebar
            "
          />

          <div v-if="selectedTaskIndex >= 0" class="w-14 text-center">
            {{ (selectedTaskIndex || 0) + 1 }}

            <template v-if="currentCollectionTasksTotal">
              of
              {{ currentCollectionTasksTotal }}
            </template>

            <template v-else>
              <Button
                v-if="
                  !profile.isEmailSenderSetup &&
                  isReplyRequired &&
                  !componentTask?.isSmsSource &&
                  componentTask?.isOpen
                "
                variant="agent"
                :disabled="isSubmitting"
                size="sm"
                @click="linkEmailDialogRef.open()"
              >
                Link Email To Respond
              </Button>

              <template v-if="isReplyRequired">
                <Tooltip v-if="taskHitlEnabled && componentTask?.isOpen" :delay-duration="0">
                  <TooltipTrigger as-child>
                    <Button
                      size="sm"
                      :disabled="componentTask?.isProcessing || isSubmitting"
                      :variant="editMode ? 'ghost' : undefined"
                      @click="toggleEditMode"
                    >
                      <template v-if="editMode">
                        <i class="far fa-arrow-left"></i>
                        Exit edit mode
                      </template>

                      <template v-else>
                        <i class="far fa-pen-to-square"></i>
                        Edit Draft
                      </template>
                    </Button>
                  </TooltipTrigger>

                  <TooltipContent>
                    {{ editMode ? 'Exit full panel editor' : 'Open full panel editor' }}
                  </TooltipContent>
                </Tooltip>

                <Tooltip
                  v-else
                  :disabled="Boolean(isSenderParticipant) || componentTask?.isSmsSource"
                >
                  <TooltipTrigger as-child>
                    <Button
                      size="sm"
                      :disabled="
                        !componentTask?.isOpen ||
                        (!componentTask?.isSmsSource &&
                          !canReplyWithoutThreadParticipation &&
                          !isSenderParticipant) ||
                        isSubmitting ||
                        isResponseGenerating ||
                        componentTask?.hasUploadingAttachments
                      "
                      @click="openConfirmDialog"
                    >
                      <template v-if="componentTask?.isProcessing">
                        <i class="fa-regular fa-spinner text-xs animate-spin"></i>Sending...
                      </template>

                      <template v-else>
                        <i class="far fa-paper-plane"></i>
                        Send
                      </template>
                    </Button>
                  </TooltipTrigger>

                  <TooltipContent>
                    <template v-if="!canReplyWithoutThreadParticipation && !isSenderParticipant">
                      You cannot respond because you are not a participant on this thread.
                    </template>
                  </TooltipContent>
                </Tooltip>
              </template>

              <Button
                v-else-if="componentTask?.isDisputeAnyCreate || componentTask?.isDisputeUpdate"
                :disabled="!componentTask?.isOpen || isSubmitting"
                :loading="isSubmitting"
                :edge="false"
                size="sm"
                @click="handleDisputeTask"
              >
                <i class="fas fa-plus-circle"></i>
                <span>{{ TaskTypeMap[componentTask?.type]?.actionBtn }}</span>
              </Button>

              <Button
                v-else-if="componentTask?.isDisputeResolution"
                :disabled="!componentTask?.isOpen || isSubmitting"
                :loading="isSubmitting"
                :edge="false"
                size="sm"
                @click="resolveDisputeTask"
              >
                <i class="fas fa-plus-circle"></i>
                <span>{{ TaskTypeMap[componentTask?.type]?.actionBtn }}</span>
              </Button>

              <Button
                v-else-if="componentTask?.isNewContact"
                type="submit"
                size="sm"
                :disabled="!componentTask?.isOpen || isSubmitting"
                @click="TaskNewContactRef?.submit"
              >
                <template v-if="componentTask?.isProcessing || TaskNewContactRef?.isSubmitting">
                  <i class="fa-regular fa-spinner animate-spin"></i>Adding contact...
                </template>

                <template v-else>Add contact</template>
              </Button>

              <Button
                v-else-if="componentTask?.isExpectsComplete"
                size="sm"
                :disabled="!componentTask?.isOpen"
                :loading="isSubmitting"
                @click="handleCompleteTask"
              >
                <i class="fas fa-check text-xs"></i>
                <span> Mark as complete </span>
              </Button>

              <Button
                v-else-if="componentTask?.isExpectsAcknowledge"
                size="sm"
                :disabled="!componentTask?.isOpen"
                :loading="isSubmitting"
                @click="handleAcknowledge"
              >
                <i class="fas fa-thumbs-up text-xs" />
                <span>Acknowledge!</span>
              </Button>

              <Button
                v-else-if="
                  currentModalTaskType === 'payment_promise' ||
                  currentModalTaskType === 'disable_contact' ||
                  (currentModalTaskType === 'call_analysis' &&
                    !taskCollectionStore?.currentTask?.isReplyRequired) ||
                  (taskCollectionStore?.currentTask?.isPaymentPromise &&
                    taskCollectionStore?.currentTask?.isActionRequired)
                "
                size="sm"
                :disabled="!componentTask?.isOpen || isSubmitting"
                :loading="isSubmitting"
                @click="handleAcknowledge"
              >
                <i class="fas fa-thumbs-up text-xs" />
                <span>Acknowledge!</span>
              </Button>

              <Button
                v-else-if="componentTask?.isEscalation"
                variant="warning"
                size="sm"
                :disabled="!componentTask?.isOpen"
                :loading="isSubmitting"
                @click="completeTask"
              >
                <i class="fas fa-check text-xs"></i>
                <span>Mark as complete</span>
              </Button>

              <Tooltip :delay-duration="0">
                <TooltipTrigger as-child>
                  <Button
                    variant="secondary"
                    size="sm"
                    :disabled="!componentTask?.isOpen || isSubmitting"
                    :loading="isSubmitting"
                    @click="openDismissAlert('close')"
                  >
                    <span class="fa-stack">
                      <i class="fa-solid fa-slash fa-stack-1x"></i>
                      <i class="fas fa-list-ul fa-stack-1x"></i>
                    </span>

                    <span>{{
                      TaskTypeMap[componentTask?.type as string]?.cancelBtn || 'Dismiss'
                    }}</span>
                  </Button>
                </TooltipTrigger>

                <TooltipContent> Mark task as closed </TooltipContent>
              </Tooltip>
            </template>
          </div>

          <Button
            class="shrink-0 group"
            size="icon-sm"
            variant="outline"
            :disabled="taskCollectionStore?.isLoadingCollectionTasks"
            @click="taskCollectionStore?.gotoPreviousTask(router, route)"
          >
            <i class="far fa-arrow-left"></i>
          </Button>

          <Button
            class="shrink-0 group"
            size="icon-sm"
            variant="outline"
            :disabled="taskCollectionStore?.isLoadingCollectionTasks"
            @click="taskCollectionStore?.gotoNextTask()"
          >
            <i class="far fa-arrow-right"></i>
          </Button>
        </template>

        <Button class="shrink-0 group" size="icon-sm" variant="outline" @click="closeDrawer">
          <i class="fa far fa-close"></i>
        </Button>
      </div>
    </div>
  </div>

  <TaskDismissAlert
    v-if="componentTask"
    :task="componentTask"
    :show="showDismissAlert"
    :action-type="dismissAlertActionType"
    @close="closeDismissAlert"
  />

  <AlertDialog :open="showConfirmDialog" @update:open="(status) => (showConfirmDialog = status)">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>
          Send {{ componentTask?.isSmsSource ? 'SMS' : 'email' }} response
        </AlertDialogTitle>
        <AlertDialogDescription>
          <span>
            Are you sure you want to send this
            {{ componentTask?.isSmsSource ? 'SMS' : 'email' }} and complete the task?
          </span>
        </AlertDialogDescription>
      </AlertDialogHeader>

      <AlertDialogFooter>
        <AlertDialogCancel :disabled="isSubmitting" @click="closeConfirmDialog">
          Cancel
        </AlertDialogCancel>

        <AlertDialogAction :loading="isSubmitting" @click="handleConfirmClick">
          Send
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>

  <AlertDialog
    :open="showCompleteWarning"
    @update:open="(status) => (showCompleteWarning = status)"
  >
    <AlertDialogContent>
      <AlertDialogHeader class="space-y-2.5">
        <AlertDialogTitle>Are you sure?</AlertDialogTitle>
        <AlertDialogDescription class="space-y-2.5">
          <strong>
            You want to continue without assigning a new primary
            {{ componentTask?.source }} contact?
          </strong>

          <p>
            Without a primary {{ componentTask?.source }} all future outreach to

            <strong v-if="componentTask?.partner?.name">
              {{ componentTask?.partner?.name }}
            </strong>

            <template v-else> this partner </template>

            via {{ componentTask?.source }} will be disabled.
          </p>
        </AlertDialogDescription>
      </AlertDialogHeader>

      <AlertDialogFooter>
        <AlertDialogCancel :disabled="isSubmitting" @click="showCompleteWarning = false">
          Cancel
        </AlertDialogCancel>

        <AlertDialogAction :loading="isSubmitting" @click="completeTask">
          Continue
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>

  <AlertDialog :open="showCancelAlert" @update:open="(status) => (showCancelAlert = status)">
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Are you sure?</AlertDialogTitle>
        <AlertDialogDescription>
          <span> Are you sure you want to cancel? You will lose any unsaved work </span>
        </AlertDialogDescription>
      </AlertDialogHeader>

      <AlertDialogFooter>
        <AlertDialogCancel @click="showCancelAlert = false"> Cancel </AlertDialogCancel>

        <AlertDialogAction @click="toggleEditMode"> Discard changes </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>

  <LinkEmailDialog ref="linkEmailDialogRef" />

  <EmailThreadDrawer
    v-if="emailThread && showEmailThread"
    v-model:open="showEmailThread"
    :thread="emailThread"
    :selected-email-id="email?.id"
    is-slider
    default-open
    class="h-full min-h-full"
    @close="
      () => {
        emailThread = null;
        showEmailThread = false;
      }
    "
  />
</template>

<style>
  :root {
    --left-width: 250px;
  }
</style>

<style scoped>
  .text-primary mark {
    color: var(--agent);
  }

  .bg-blue-100 mark {
    background-color: var(--blue);
    color: var(--blue);
  }

  .grow-enter-active {
    transition: all 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }

  .grow-leave-active {
    transition: all 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
  }

  .grow-enter-from,
  .grow-leave-to {
    max-height: 0;
    opacity: 0;
    transform: translateY(-10px);
  }

  .grow-enter-to,
  .grow-leave-from {
    max-height: 300px;
    opacity: 1;
    transform: translateY(0);
  }

  .shrink-enter-active {
    transition: all 300ms cubic-bezier(0.34, 1.56, 0.64, 1);
    overflow: hidden;
  }

  .shrink-leave-active {
    transition: all 300ms cubic-bezier(0.25, 0.46, 0.45, 0.94);
    overflow: hidden;
  }

  .shrink-enter-from,
  .shrink-leave-to {
    max-height: 0;
    opacity: 0;
    transform: translateY(-10px);
  }

  .shrink-enter-to,
  .shrink-leave-from {
    max-height: 300px;
    opacity: 1;
    transform: translateY(0);
  }

  /* Forward task content transition animations */
  .task-content-forward-enter-active {
    transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }

  .task-content-forward-leave-active {
    transition: all 0.3s cubic-bezier(0.55, 0.085, 0.68, 0.53);
  }

  .task-content-forward-enter-from {
    opacity: 0;
    transform: translateX(100%) scale(1);
  }

  .task-content-forward-leave-to {
    opacity: 0;
    transform: translateX(-100%) scale(1);
  }

  .task-content-forward-enter-to,
  .task-content-forward-leave-from {
    opacity: 1;
    transform: translateX(0) translateY(0) scale(1);
  }

  /* Backward task content transition animations */
  .task-content-backward-enter-active {
    transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  }

  .task-content-backward-leave-active {
    transition: all 0.3s cubic-bezier(0.55, 0.085, 0.68, 0.53);
  }

  .task-content-backward-enter-from {
    opacity: 0;
    transform: translateX(-100%) scale(1);
  }

  .task-content-backward-leave-to {
    opacity: 0;
    transform: translateX(100%) scale(1);
  }

  .task-content-backward-enter-to,
  .task-content-backward-leave-from {
    opacity: 1;
    transform: translateX(0) translateY(0) scale(1);
  }
</style>
